import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Onboarding store with localStorage persistence
 * Tracks user onboarding state, progress, and form data
 *
 * Flow States:
 * - null: Initial state, checking user status
 * - not_started: Profile doesn't exist, show website form
 * - analyzing: Profile analysis in progress
 * - profile_ready: Profile created, ready for summary/offerings edit
 * - generating_post: Summary/offerings saved, post generation in progress
 * - completed: First post ready, redirect to dashboard
 * - failed: Error occurred during profile creation
 */
export const useOnboardingStore = create(
  persist(
    (set, get) => ({
      // User email
      userEmail: null,

      // Current onboarding step (0: getting started, 1: Business DNA, 2: offerings, 3: visual identity)
      currentStep: 0,

      // Website URL input
      websiteUrl: '',

      // How the profile creation was started: 'website' | 'manual' | null
      profileCreationSource: null,

      // Flow state for tracking onboarding progress
      flowState: null, // null | 'not_started' | 'analyzing' | 'profile_ready' | 'generating_post' | 'completed' | 'failed'

      // Profile creation error
      profileError: null,

      // Analysis progress (0-100)
      analysisProgress: 0,

      // Guide visibility flag (for future implementation)
      shouldShowGuide: true,

      // First time login flag
      firstTimeLogin: true,

      // Flag to track if we should show success overlay
      showSuccessOverlay: false,

      // Actions
      setUserEmail: (email) => set({ userEmail: email }),

      setCurrentStep: (step) => set({ currentStep: step }),

      setWebsiteUrl: (url) => set({ websiteUrl: url }),

      setFlowState: (state) => set({ flowState: state }),

      setProfileError: (error) => set({ profileError: error, flowState: 'failed' }),

      setAnalysisProgress: (progress) => set({ analysisProgress: progress }),

      setShouldShowGuide: (show) => set({ shouldShowGuide: show }),

      setFirstTimeLogin: (isFirst) => set({ firstTimeLogin: isFirst }),

      setShowSuccessOverlay: (show) => set({ showSuccessOverlay: show }),

      // Start profile analysis
      startAnalysis: (url, source = 'website') =>
        set({
          websiteUrl: url,
          profileCreationSource: source,
          flowState: 'analyzing',
          profileError: null,
          analysisProgress: 0,
          currentStep: 1,
        }),

      // Mark profile as ready
      markProfileReady: () =>
        set((state) => ({
          flowState: 'profile_ready',
          analysisProgress: 100,
          currentStep: Math.max(state.currentStep, 1),
          profileError: null,
        })),

      // Start post generation (when advancing to First post step)
      startPostGeneration: () =>
        set({
          flowState: 'generating_post',
          currentStep: 4,
        }),

      // Retry profile creation (reuse stored website URL)
      retryProfileCreation: () => {
        const { websiteUrl } = get();
        set({
          flowState: 'analyzing',
          profileError: null,
          analysisProgress: 0,
          currentStep: 0,
        });
        return websiteUrl;
      },

      // Reset onboarding state but keep email
      resetOnboarding: () =>
        set({
          currentStep: 0,
          websiteUrl: '',
          profileCreationSource: null,
          flowState: 'not_started',
          profileError: null,
          analysisProgress: 0,
          shouldShowGuide: true,
          firstTimeLogin: true,
          showSuccessOverlay: false,
        }),

      // Mark onboarding as completed
      completeOnboarding: () =>
        set({
          flowState: 'completed',
          shouldShowGuide: true,
          firstTimeLogin: false,
          showSuccessOverlay: true,
          currentStep: 3,
        }),

      // Clear profile error
      clearProfileError: () => set({ profileError: null }),
    }),
    {
      name: 'onboarding-storage', // localStorage key
      version: 3, // version bumped when step indices changed
    }
  )
);
